/**
 * ProfileReportingTab — the Reporting tab on the area, resident, device and user
 * profiles: hourly and weekly charts, the event reason breakdown and the 25 most recent
 * events (docs/DUPLICATION_AUDIT.md, B18; like ProfileLogsTable for the Logs tabs).
 *
 * Replaces four local ReportingTab copies. The area, resident and device ones differed
 * only in wording, their Run Report filter and which link columns the Recent Events
 * table shows; the user one had drifted (no ⓘ tips, a plain link for Run Report, no
 * summary line, plain reason text, a button where the others have NotesCell).
 *
 * Reuses the V1 chart components HourlyLineAndBarChart and LineTrendingChart.
 *
 * @param {'area'|'resident'|'device'|'user'} entity
 * @param {number|string} entityId — for Run Report's filter
 * @param {(eventIndex: number, event: object) => void} onAddNote
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import HourlyLineAndBarChart from '../charts/HourlyLineAndBarChart.jsx';
import LineTrendingChart from '../charts/LineTrendingChart.jsx';
import RunReportButton from './RunReportButton.jsx';
import NotesCell from './NotesCell.jsx';
import CallsCell from './CallsCell.jsx';
import ReasonCell from './ReasonCell.jsx';
import HeaderInfoTip from './HeaderInfoTip.jsx';
import { convertIsoToLocal } from '../../libs.jsx';
import { getAlertDeviceLink } from '../../utils/deviceLinks.js';
import { getRoleWeight } from '../../services/session.js';

/* Per entity: the tip wording, the Run Report filter key, and the Recent Events link
   columns (in order). `deviceLabel` differs for the user tab: its events carry the
   device name as `description`. */
const ENTITIES = {
    area: { noun: 'area', triggered: 'triggered in this area', filterKey: 'areas', columns: [['device', 'Device'], ['resident', 'Resident']] },
    resident: { noun: 'resident', triggered: 'triggered by this resident', filterKey: 'residents', columns: [['device', 'Device'], ['area', 'Location']] },
    device: { noun: 'device', triggered: 'triggered by this device', filterKey: 'devices', columns: [['resident', 'Resident'], ['area', 'Location']] },
    user: {
        noun: 'user', triggered: 'for this user', filterKey: 'users',
        columns: [['device', 'Device'], ['resident', 'Resident'], ['area', 'Area']],
        deviceLabel: 'description',
    },
};

const DASH = '—';
const linkStyle = { fontSize: 'var(--font-size-sm)' };

function tips(e) {
    return {
        hourly: `This chart shows the average number of alerts per hour of the day (bars) and the average caregiver response time, in minutes, for those alerts (line) for this ${e.noun} over the last 28 days. The dashed horizontal lines mark the overall averages across all hours so you can quickly spot which times of day run higher or lower than normal.`,
        weekly: `This chart shows the total number of alerts ${e.triggered} each week over the last 12 weeks, so you can spot trends in alert volume over time.`,
        reason: `This table breaks down events ${e.triggered} over the last 28 days by reason code, showing the count, share of total volume, and average caregiver response time (in minutes) for each reason. The Total Events row at the top is the rolled-up total across all reasons.`,
        recent: `This table lists the 25 most recent events for this ${e.noun}, regardless of date range, so you can quickly review the latest activity and jump to any individual event for more detail.`,
    };
}

function DeviceLinkCell({ evt, entity }) {
    const label = evt[entity.deviceLabel || 'device_name'] || DASH;
    // Alert rule on every tab: a System Outage event in an area's Recent Events
    // carries a palcare_device_id and must open the server profile, not a device.
    const link = getAlertDeviceLink(evt, getRoleWeight());
    const uuidSub = evt.manufacture_uuid ? (
        <span className="dp-cell-text dp-cell-text--muted" style={{ display: 'block', fontSize: 'var(--font-size-xs)' }}>
            {evt.manufacture_uuid}
        </span>
    ) : null;
    // Link only for devices with a profile page; otherwise the name as plain text.
    return link ? (
        <Link to={link} className="pp-field__value--link" style={linkStyle}>
            <span>{label}</span>
            {uuidSub}
        </Link>
    ) : (
        <div>
            <span className="dp-cell-text">{label}</span>
            {uuidSub}
        </div>
    );
}

function EntityLinkCell({ id, name, to }) {
    return id ? (
        <Link to={to} className="pp-field__value--link" style={linkStyle}>{name || DASH}</Link>
    ) : (
        <span className="dp-cell-text dp-cell-text--muted">{DASH}</span>
    );
}

const ProfileReportingTab = ({
    entity: entityKey, entityId,
    hourData, weekData, reasonData, eventData,
    eventNotesActive, isReasonEditable = false, isGrandstream, tzOffset, onAddNote,
}) => {
    const entity = ENTITIES[entityKey];
    const tip = useMemo(() => tips(entity), [entity]);
    const events = eventData || [];

    // Reason rows: { reasons: [...] }, or a bare array (the user report returns that)
    const reasonRows = useMemo(() => {
        if (Array.isArray(reasonData?.reasons)) return reasonData.reasons;
        if (Array.isArray(reasonData)) return reasonData;
        return [];
    }, [reasonData]);

    // Weekly chart data (formatted for LineTrendingChart)
    const weekChartProps = useMemo(() => ({
        lineData: [weekData?.events?.length > 0 ? weekData.events : []],
        lineColors: ['#0081C7'],
        lineLabels: ['Total Alarms'],
        axisLabels: weekData?.labels?.length > 0 ? weekData.labels : [],
    }), [weekData]);

    const cell = (kind, evt) => {
        if (kind === 'device') return <DeviceLinkCell evt={evt} entity={entity} />;
        if (kind === 'resident') return <EntityLinkCell id={evt.resident_id} name={evt.resident_name} to={`/community/residents/${evt.resident_id}`} />;
        return <EntityLinkCell id={evt.area_id} name={evt.area_name} to={`/community/areas/${evt.area_id}`} />;
    };

    return (
        <>
            {/* Report action + summary stats */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-success-text)', fontWeight: 'var(--font-weight-semibold)' }}>
                    {!isNaN(hourData?.avgValue) ? `${hourData.avgValue} Alert/Week` : ''}
                    {!isNaN(weekData?.avgValue) ? ` · ${weekData.avgValue} Avg Resp` : ''}
                </span>
                <RunReportButton filters={{ [entity.filterKey]: [entityId] }} />
            </div>

            {/* Charts — separate cards in a responsive grid */}
            <div className="ip-chart-row" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Alarm Activity by Hour<HeaderInfoTip title="About This Chart" body={tip.hourly} /></h3>
                    </div>
                    <div className="pp-section__body">
                        <div className="ip-chart-section">
                            <HourlyLineAndBarChart
                                style={{ maxHeight: '350px' }}
                                labels={hourData?.labels || Array.from({ length: 24 }, (_, i) => String(i))}
                                x_axis={true}
                                x_axis_title="Time of the Day"
                                bar_axis={true}
                                bar_axis_title="Total Alarms"
                                bar_color="#0081C7"
                                line_axis={true}
                                line_axis_title="Average Response Time"
                                line_color="#BE4D25"
                                bar_data={hourData?.events || []}
                                line_data={hourData?.responses || []}
                                bar_annotation={true}
                                line_annotation={true}
                                forceAspect={true}
                                bar_annotation_value={hourData?.avgValue || 0}
                                line_annotation_value={hourData?.avgResp || 0}
                            />
                        </div>
                    </div>
                </div>

                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Weekly Alarm Trend<HeaderInfoTip title="About This Chart" body={tip.weekly} /></h3>
                    </div>
                    <div className="pp-section__body">
                        <div className="ip-chart-section">
                            <LineTrendingChart
                                style={{ maxHeight: '350px' }}
                                x_axis={true}
                                x_axis_title="Weeks"
                                line_axis={true}
                                line_axis_title="Total Alarms"
                                lines={1}
                                line_color={weekChartProps.lineColors}
                                line_labels={weekChartProps.lineLabels}
                                line_data={weekChartProps.lineData}
                                forceAspect={true}
                                labels={weekChartProps.axisLabels}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Reason Breakdown + Recent Events */}
            <div className="ip-report-grid">
                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Event Reason Breakdown<HeaderInfoTip title="About This Table" body={tip.reason} /></h3>
                    </div>
                    <div className="pp-section__body pp-section__body--flush">
                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th">Reason</th>
                                        <th className="dp-th dp-th--center">Total</th>
                                        <th className="dp-th dp-th--center">% Total Volume</th>
                                        <th className="dp-th dp-th--center">Avg Response (min)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Summary row */}
                                    <tr className="dp-tr ip-reason-total">
                                        <td className="dp-td"><span className="dp-cell-text">Total Events</span></td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{reasonData?.count || 0}</span>
                                        </td>
                                        <td className="dp-td dp-td--center"></td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{reasonData?.avg_response_time || '0.0'}</span>
                                        </td>
                                    </tr>
                                    {reasonRows.map((row, idx) => (
                                        <tr key={idx} className="dp-tr">
                                            <td className="dp-td"><span className="dp-cell-text">{row.reason_name || row.reason_code_description || DASH}</span></td>
                                            <td className="dp-td dp-td--center">
                                                <span className="dp-cell-text">{row.count || 0}</span>
                                            </td>
                                            <td className="dp-td">
                                                <div className="ip-percent-bar">
                                                    <div className="ip-percent-bar__track">
                                                        <div className="ip-percent-bar__fill" style={{ width: `${parseFloat(row.percent) || 0}%` }} />
                                                    </div>
                                                    <span className="ip-percent-bar__label">{row.percent || DASH}</span>
                                                </div>
                                            </td>
                                            <td className="dp-td dp-td--center">
                                                <span className="dp-cell-text">{row.avg_response_time || DASH}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    {reasonRows.length === 0 ? (
                                        <tr className="dp-tr">
                                            <td className="dp-td dp-td--center" colSpan={4}>
                                                <span className="dp-cell-text dp-cell-text--muted">No event data for this period</span>
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Recent Events<HeaderInfoTip title="About This Table" body={tip.recent} /></h3>
                    </div>
                    <div className="pp-section__body pp-section__body--flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th">Event Start</th>
                                        <th className="dp-th dp-th--center">Resp (min)</th>
                                        {entity.columns.map(([kind, label]) => <th key={kind} className="dp-th">{label}</th>)}
                                        <th className="dp-th dp-th--center">Reason</th>
                                        {eventNotesActive ? <th className="dp-th dp-th--center">Notes</th> : null}
                                        {isGrandstream ? <th className="dp-th dp-th--center">Calls</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((evt, idx) => (
                                        <tr key={evt.event_id || idx} className="dp-tr">
                                            <td className="dp-td td-wrap">
                                                <Link to={`/profiles/event/${evt.event_id}`} className="pp-field__value--link" style={linkStyle}>
                                                    {evt.event_datetime_local ? convertIsoToLocal(evt.event_datetime_local) : DASH}
                                                </Link>
                                            </td>
                                            <td className="dp-td dp-td--center">
                                                <span className="dp-cell-text">{evt.response_time ?? DASH}</span>
                                            </td>
                                            {entity.columns.map(([kind]) => (
                                                <td key={kind} className="dp-td td-wrap">{cell(kind, evt)}</td>
                                            ))}
                                            <td className="dp-td td-wrap dp-td--center">
                                                <ReasonCell
                                                    reasonId={evt.reason_id}
                                                    description={evt.reason_code_description}
                                                    edited={isReasonEditable && evt.reason_edits?.length > 0}
                                                />
                                            </td>
                                            {eventNotesActive ? (
                                                <td className="dp-td dp-td--center">
                                                    <NotesCell
                                                        row={evt}
                                                        notes={evt.notes}
                                                        onClick={() => onAddNote(idx, evt)}
                                                        eventNotesActive={eventNotesActive}
                                                        tzOffset={tzOffset}
                                                        ariaPrefix={`Event ${evt.event_id || ''}`}
                                                    />
                                                </td>
                                            ) : null}
                                            {isGrandstream ? (
                                                <td className="dp-td dp-td--center">
                                                    <CallsCell calls={evt.calls} tzOffset={tzOffset} ariaPrefix={`Event ${evt.event_id || ''}`} />
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                    {events.length === 0 ? (
                                        <tr className="dp-tr">
                                            <td className="dp-td dp-td--center" colSpan={3 + entity.columns.length + (eventNotesActive ? 1 : 0) + (isGrandstream ? 1 : 0)}>
                                                <span className="dp-cell-text dp-cell-text--muted">No recent events</span>
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfileReportingTab;
